import { Field, Input } from "./ui";
import { useTranslation } from "react-i18next";
import { MIN_INTERVAL_MINUTES, PRESET_CRONS, nextRun, validateCron } from "../lib/cron";
import { absoluteTime, relativeTime } from "../lib/format";

/**
 * A cron input that says what it will do, while it is being typed.
 *
 * Cron is a notation most people meet once a year and never memorise, so the
 * field carries three things a bare text box does not: presets for the four
 * shapes almost everybody wants, a plain-English reading of whatever is in the
 * box, and the reason it is refused when it is wrong.
 *
 * The check here is the same parser the API runs, copied into this repository —
 * see lib/cron. It is a convenience and never a gate: the API validates again
 * on every write, so the worst a drift between them can do is make this form
 * accept something the server then refuses.
 */
export function CronField({
  value,
  onChange,
  timezone,
  disabled,
}: {
  value: string;
  onChange: (value: string) => void;
  timezone: string;
  disabled?: boolean;
}) {
  const { t } = useTranslation("schedules");
  const check = validateCron(value);
  const touched = value.trim().length > 0;
  // Computed only once the expression parses, so a half-typed field is not
  // repeatedly walked forward four years looking for a match it cannot have.
  const upcoming = check.ok ? nextRun(value, timezone) : null;

  return (
    <Field
      label={t("cron.label")}
      error={touched && !check.ok ? check.reason : undefined}
    >
      <div className="space-y-2">
        <Input
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder={t("cron.placeholder")}
          spellCheck={false}
          autoComplete="off"
          className="font-mono"
          disabled={disabled}
        />

        {/*
          The constraint sits under the input it constrains, ahead of the
          presets and the preview. Passed to `Field` as a hint it rendered last
          — after the preview — so the page explained the field only once the
          reader had already been shown its answer.
        */}
        <p className="text-[13px] text-fg-subtle">
          Standard cron, five fields. Runs at most once every {MIN_INTERVAL_MINUTES} minutes.
        </p>

        <div className="flex flex-wrap gap-1.5">
          {PRESET_CRONS.map((preset) => (
            <button
              key={preset.cron}
              type="button"
              disabled={disabled}
              onClick={() => onChange(preset.cron)}
              className={
                "rounded-md border px-2.5 py-1 text-[12px] transition-colors disabled:opacity-50 " +
                (value === preset.cron
                  ? "border-accent/40 bg-accent/10 text-accent"
                  : "border-line bg-raised text-fg-muted hover:border-line-strong hover:text-fg")
              }
            >
              {t(`preset.${preset.key}` as "preset.daily")}
            </button>
          ))}
        </div>

        {/*
          The reading, not the expression. Somebody who typed `0 3 * * 1` needs
          to see "Every Monday at 03:00" to catch that they meant Sunday — and
          the timezone belongs beside it, because 03:00 in the wrong zone is the
          other half of that mistake.
        */}
        {check.ok && (
          <div className="rounded-md border border-line bg-raised/50 px-3 py-2">
            <p className="text-[13px] text-fg">
              {check.description} <span className="text-fg-subtle">({timezone})</span>
            </p>
            {/*
              The first firing, spelled out. "Every Monday at 03:00" is what the
              expression says; "in 5 days" is the thing somebody is actually
              checking, and it is where a wrong weekday or a wrong timezone
              becomes obvious before the job is created rather than after it
              fails to run.
            */}
            {upcoming && (
              <p className="mt-1 text-[12px] text-fg-subtle">
                First run {relativeTime(upcoming.toISOString())} ·{" "}
                {absoluteTime(upcoming.toISOString())}
              </p>
            )}
          </div>
        )}
      </div>
    </Field>
  );
}
